/*
 * cgrid-facets.js — shared custom AJAX faceting for the .cgrid layout (collection + search), no Dawn.
 * Data-attribute driven so ONE engine serves every faceted grid (DRY).
 *   [data-facet-root]  data-facet-base="<results url>"  data-facet-section="<section id>"
 *   [data-facet-form]   the <form> wrapping the facet inputs
 *   [data-facet-sort]   sort <select>(s)
 *   [data-facet-grid]   product-grid container (inner swapped)
 *   [data-facet-count]  product-count element(s) (inner swapped)
 *   [data-facet-active] active-chips container(s) (inner swapped)
 *   [data-facet-url]    chip remove link (AJAX-intercepted)   [data-facet-clear] clear-all
 *   drawer: [data-facet-toggle] [data-facet-drawer] [data-facet-overlay] [data-facet-close] [data-facet-apply]
 */
(function () {
  'use strict';

  function initRoot(root) {
    if (root.dataset.facetBound) return;
    root.dataset.facetBound = '1';
    var BASE = root.getAttribute('data-facet-base') || '';
    var SID = root.getAttribute('data-facet-section') || '';
    var renderRequestId = 0;
    var renderController = null;
    var priorityParams = { Row: '_cgrid_row', Color: '_cgrid_color' };
    var priorityHistory = { Row: [], Color: [] };

    function form() { return root.querySelector('[data-facet-form]'); }
    function sorts() { return root.querySelectorAll('[data-facet-sort]'); }

    var scrollbarTargets = Array.prototype.slice.call(
      root.querySelectorAll('.cgrid-sidebar, .cgrid-drawer-scrollable')
    );
    var scrollbarStates = scrollbarTargets.map(function () {
      return { current: null, target: 0 };
    });
    var scrollbarMeasureFrame = 0;
    var scrollbarAnimationFrame = 0;
    var reduceScrollbarMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function animateScrollbarProgress() {
      scrollbarAnimationFrame = 0;
      var keepAnimating = false;

      scrollbarTargets.forEach(function (scroller, index) {
        var state = scrollbarStates[index];
        if (state.current === null || reduceScrollbarMotion) {
          state.current = state.target;
        } else {
          var difference = state.target - state.current;
          if (Math.abs(difference) <= 0.1) {
            state.current = state.target;
          } else {
            state.current += difference * 0.2;
            keepAnimating = true;
          }
        }
        scroller.style.setProperty('--cgrid-scrollbar-progress', state.current.toFixed(3) + '%');
      });

      if (keepAnimating) {
        scrollbarAnimationFrame = requestAnimationFrame(animateScrollbarProgress);
      }
    }

    function syncScrollbarProgress() {
      scrollbarMeasureFrame = 0;
      scrollbarTargets.forEach(function (scroller, index) {
        var maxScroll = scroller.scrollHeight - scroller.clientHeight;
        var progress = maxScroll > 0 ? scroller.scrollTop / maxScroll : 0;
        scrollbarStates[index].target = Math.max(0, Math.min(1, progress)) * 100;
      });

      if (!scrollbarAnimationFrame) {
        scrollbarAnimationFrame = requestAnimationFrame(animateScrollbarProgress);
      }
    }

    function scheduleScrollbarSync() {
      if (scrollbarMeasureFrame) return;
      scrollbarMeasureFrame = requestAnimationFrame(syncScrollbarProgress);
    }

    function initScrollbarProgress() {
      scrollbarTargets.forEach(function (scroller) {
        scroller.addEventListener('scroll', scheduleScrollbarSync, { passive: true });
      });
      root.addEventListener('toggle', function (event) {
        if (event.target.matches && event.target.matches('.cgrid-filter-group')) {
          scheduleScrollbarSync();
        }
      }, true);
      window.addEventListener('resize', scheduleScrollbarSync);
      scheduleScrollbarSync();
    }

    function activeLabels(group) {
      var labels = [];
      var f = form();
      if (!f) return labels;
      f.querySelectorAll('input[type="checkbox"][data-facet-label="' + group + '"]:checked').forEach(function (input) {
        var label = input.getAttribute('data-facet-value-label');
        if (label && labels.indexOf(label) < 0) labels.push(label);
      });
      return labels;
    }

    function syncPriority(group) {
      var active = activeLabels(group);
      priorityHistory[group] = priorityHistory[group].filter(function (label) { return active.indexOf(label) >= 0; });
      active.forEach(function (label) {
        if (priorityHistory[group].indexOf(label) < 0) priorityHistory[group].push(label);
      });
    }

    function seedPriorities(url) {
      var parsed = new URL(url || location.href, location.origin);
      Object.keys(priorityParams).forEach(function (group) {
        var saved = parsed.searchParams.get(priorityParams[group]);
        priorityHistory[group] = saved ? saved.split('~~').filter(Boolean) : [];
        syncPriority(group);
      });
    }

    function updatePriority(input) {
      var group = input.getAttribute('data-facet-label');
      if (!priorityParams[group]) return;
      var label = input.getAttribute('data-facet-value-label');
      priorityHistory[group] = priorityHistory[group].filter(function (item) { return item !== label; });
      if (input.checked && label) priorityHistory[group].unshift(label);
      syncPriority(group);
    }

    function removePriority(group, label) {
      if (!priorityParams[group] || !label) return;
      priorityHistory[group] = priorityHistory[group].filter(function (item) { return item !== label; });
    }

    function addPriorities(url) {
      var parsed = new URL(url, location.origin);
      Object.keys(priorityParams).forEach(function (group) {
        var orderedActive = priorityHistory[group];
        if (orderedActive.length) parsed.searchParams.set(priorityParams[group], orderedActive.join('~~'));
        else parsed.searchParams.delete(priorityParams[group]);
      });
      return parsed.pathname + (parsed.search ? parsed.search : '') + (parsed.hash ? parsed.hash : '');
    }

    function buildUrl() {
      var params = new URLSearchParams();
      var f = form();
      if (f) new FormData(f).forEach(function (v, k) { if (v !== '' && v != null) params.append(k, v); });
      var s = '';
      sorts().forEach(function (sel) { if (sel.value) s = sel.value; });
      if (s) params.set('sort_by', s);
      var qs = params.toString();
      return addPriorities(BASE + (qs ? (BASE.indexOf('?') >= 0 ? '&' : '?') + qs : ''));
    }

    function swapRegion(doc, attr) {
      var news = doc.querySelectorAll('[' + attr + ']');
      var curs = root.querySelectorAll('[' + attr + ']');
      curs.forEach(function (cur, i) { if (news[i]) cur.innerHTML = news[i].innerHTML; });
    }

    function setLoading(on) { root.classList.toggle('cgrid-is-loading', on); }

    /* Scroll restoration ---------------------------------------------------
     * Narrowing a filter shrinks the page under the reader — 246 results down to
     * 2 takes ~4600px out of the document — so the browser clamps their scroll to
     * the new bottom, which is the footer. Put them back at the start of the
     * results instead. Mirrors the read-more handler in collection-content.
     */
    var scrollPending = false;

    // Sticky header covers the top of the viewport once it re-appears on scroll up.
    function headerOffset() {
      var header = document.querySelector('.section-header');
      if (!header) return 0;
      var pos = getComputedStyle(header).position;
      if (pos !== 'sticky' && pos !== 'fixed') return 0;
      return header.getBoundingClientRect().height;
    }

    function scrollToResults() {
      var d = root.querySelector('[data-facet-drawer]');
      // On mobile the drawer covers the page with the body scroll locked, so moving
      // the page now would be invisible. Defer until APPLY/close hands it back.
      if (d && d.classList.contains('open')) { scrollPending = true; return; }
      var anchor = root.querySelector('[data-facet-grid]') || root;
      requestAnimationFrame(function () {
        var top = anchor.getBoundingClientRect().top - headerOffset() - 16;
        // Only ever scroll up — someone still above the grid keeps their place.
        // Instant, not smooth: the gap can be thousands of px and the clamp has
        // already moved them, so one correction reads better than a long glide.
        if (top < 0) window.scrollBy({ top: top });
      });
    }

    function render(url, push) {
      var requestId = ++renderRequestId;
      if (renderController) renderController.abort();
      renderController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var fetchOptions = renderController ? { signal: renderController.signal } : undefined;
      setLoading(true);
      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      fetch(url + sep + 'section_id=' + SID, fetchOptions)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (requestId !== renderRequestId) return;
          renderController = null;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['data-facet-grid', 'data-facet-form', 'data-facet-count', 'data-facet-active'].forEach(function (a) { swapRegion(doc, a); });
          if (push !== false) history.pushState({ u: url }, '', url);
          setLoading(false);
          bindShowMore();
          Object.keys(priorityParams).forEach(syncPriority);
          scheduleScrollbarSync();
          // Not on popstate — back/forward should keep the browser's own scroll.
          if (push !== false) scrollToResults();
        })
        .catch(function (error) {
          if (requestId !== renderRequestId) return;
          renderController = null;
          if (!error || error.name !== 'AbortError') setLoading(false);
        });
    }
    function apply() { render(buildUrl(), true); }

    root.addEventListener('change', function (e) {
      var t = e.target;
      var f = form();
      if (f && f.contains(t)) { updatePriority(t); apply(); return; }
      if (t.matches && t.matches('[data-facet-sort]')) {
        sorts().forEach(function (sel) { if (sel !== t) sel.value = t.value; });
        apply();
      }
    });
    root.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('[data-facet-url]');
      if (chip) {
        e.preventDefault();
        removePriority(chip.getAttribute('data-facet-label'), chip.getAttribute('data-facet-value-label'));
        render(addPriorities(chip.getAttribute('data-facet-url')), true);
        return;
      }
      if (e.target.closest && e.target.closest('[data-facet-clear]')) {
        e.preventDefault();
        priorityHistory = { Row: [], Color: [] };
        render(BASE, true);
      }
    });
    window.addEventListener('popstate', function () {
      seedPriorities(location.href);
      render(location.pathname + location.search, false);
    });

    /* mobile drawer */
    var drawer = root.querySelector('[data-facet-drawer]'),
      overlay = root.querySelector('[data-facet-overlay]'),
      toggleBtn = root.querySelector('[data-facet-toggle]'),
      closeBtn = root.querySelector('[data-facet-close]'),
      applyBtn = root.querySelector('[data-facet-apply]');
    function openD() {
      if (drawer) drawer.classList.add('open');
      if (overlay) overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      scheduleScrollbarSync();
    }
    function closeD() {
      if (drawer) drawer.classList.remove('open');
      if (overlay) overlay.classList.remove('active');
      document.body.style.overflow = '';
      if (scrollPending) { scrollPending = false; scrollToResults(); }
    }
    if (toggleBtn) toggleBtn.addEventListener('click', openD);
    if (closeBtn) closeBtn.addEventListener('click', closeD);
    if (overlay) overlay.addEventListener('click', closeD);
    if (applyBtn) applyBtn.addEventListener('click', closeD);

    function initStickyBar() {
      var existingBar = root.querySelector('[data-facet-sticky]');
      if (window.innerWidth >= 990) {
        if (existingBar) {
          existingBar.remove();
        }
        return;
      }
      if (existingBar) return;

      var topbar = root.querySelector('.cgrid-topbar');
      if (topbar && 'IntersectionObserver' in window && !root.querySelector('[data-facet-sticky]')) {
        var bar = document.createElement('div');
        bar.className = 'cgrid-sticky-bar';
        bar.setAttribute('data-facet-sticky', '');
        var inner = document.createElement('div');
        inner.className = 'page-width cgrid-sticky-bar__inner';
        bar.appendChild(inner);

        var filterSrc = topbar.querySelector('[data-facet-toggle]');
        if (filterSrc) {
          var filterClone = filterSrc.cloneNode(true);
          filterClone.removeAttribute('data-facet-toggle');
          filterClone.addEventListener('click', openD);
          inner.appendChild(filterClone);
        }
        var sortSrc = topbar.querySelector('.cgrid-sort-form');
        if (sortSrc) inner.appendChild(sortSrc.cloneNode(true));

        root.appendChild(bar);

        var topbarVisible = true;
        function updateSticky() {
          if (window.innerWidth >= 990) return;

          var rect = root.getBoundingClientRect();

          var rootStillHasContent =
            rect.bottom > (bar.offsetHeight + 300);

          bar.classList.toggle(
            'is-visible',
            !topbarVisible && rootStillHasContent
          );
        }

        new IntersectionObserver(function ([entry]) {
          topbarVisible = entry.isIntersecting;
          updateSticky();
        }, {
          threshold: 0
        }).observe(topbar);

        window.addEventListener('scroll', updateSticky, { passive: true });
        window.addEventListener('resize', updateSticky);

        updateSticky();
      }
    }

    initStickyBar();
    window.addEventListener('resize', initStickyBar);

    /* show-more (re-bind after AJAX swap) */
    function bindShowMore() {
      root.querySelectorAll('.cgrid-show-more-btn').forEach(function (b) {
        if (b.dataset.bound) return; b.dataset.bound = '1';
        b.addEventListener('click', function () {
          var cls = this.dataset.target, more = this.dataset.more, less = this.dataset.less;
          var h = root.querySelectorAll('.' + cls);
          var col = h.length && h[0].classList.contains('cgrid-hidden');
          h.forEach(function (el) { el.classList.toggle('cgrid-hidden'); });
          var lbl = this.querySelector('.link-button-label');
          if (lbl) { lbl.textContent = col ? less : more; } else { this.textContent = col ? less : more; }
          scheduleScrollbarSync();
        });
      });
    }
    seedPriorities(location.href);
    bindShowMore();
    initScrollbarProgress();
  }

  function initAll() { document.querySelectorAll('[data-facet-root]').forEach(initRoot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
